import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  ACODropdown,
  ACODropdownItem,
  CheckCircleIcon,
  Dialog,
  Modal,
} from "staysure-component-library";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import RichText from "@/components/atoms/rich-text/rich-text";
import TextSelectionButton from "@/components/atoms/text-selection-button/text-selection-button";
import ToggleBtnLabelWrapper from "@/components/atoms/toggle-btn-wrapper/toggle-btn-label-wrapper";
import withApplication from "@/components/with-application";
import { GOLF_YEAR_PRICE as RENEWAL_GOLF_YEAR_PRICE } from "@/config/app-constant";
import { PRODUCT, ANNUAL_MULTI_TRIP, SINGLE_TRIP } from "@/constants";
import useMediaQuery from "@/hooks/use-media-query/use-media-query";
import { useQuoteDisabled } from "@/hooks/use-quote-disabled/use-quote-disabled";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { RootState } from "@/store/root-reducer";
import { SingleACOSectionProps } from "@/types/QuotePage/SingleAcoType";
import singleAcoClassNames from "@/utils/single-aco-section-brand-styles.utils";

import CoronaModal from "./corona-modal";
import MoreInfoDrawer from "./more-info-drawer";
import RemoveModal from "./remove-modal";
import Styles from "./single-aco-section.module.css";

export const GOLF_YEAR_PRICE = {
  avanti: 11.28,
  staysure: 15.95,
} as const;

const brand: "staysure" | "avanti" =
  (PRODUCT as "staysure" | "avanti") ?? "staysure";

const getDrawerContent = (
  code: string,
  drawerContent: string,
  isBeforeGadgetMigration?: boolean
) => {
  if (code === "GADGET_COVER" && !isBeforeGadgetMigration) {
    return drawerContent.replace(/&lt;PLACEHOLDER_GADGET_MIGRATION&gt;/g, "");
  } else if (code === "GADGET_COVER" && isBeforeGadgetMigration) {
    return drawerContent.replace(
      /&lt;PLACEHOLDER_GADGET_MIGRATION&gt;/g,
      "(excludes laptops)"
    );
  }
  return drawerContent;
};

const pricesUtil: string[] = [
  "450",
  "500",
  "550",
  "600",
  "650",
  "700",
  "750",
  "800",
  "850",
  "900",
  "950",
  "1000",
];

function SingleACOSection({
  acoTitle,
  description,
  removeModalTitle,
  removeModalDescription,
  removeModalConfirmCTALabel,
  removeModalCancelCTALabel,
  drawerContent,
  drawerCloseCta,
  addBtnLabel,
  addedBtnLabel,
  removeBtnLabel,
  moreInfoBtnLabel,
  tdeModalTitle = "",
  tdeModalCopy = "",
  tdeModalConfirmBtnLabel = "",
  tdeModalCancelBtnLabel = "",
  type = SINGLE_TRIP,
  priceYear,
  priceMonth,
  totalPCLCost,
  isGolfCover = false,
  isSingleItemCover = false,
  isTravelDisruption = false,
  isSelected = false,
  travelers = [],
  onSelect,
  id = "",
  code = "",
  application,
}: Readonly<SingleACOSectionProps>) {
  const [isSelect, setIsSelect] = React.useState(false);
  const isTablet = useMediaQuery("(min-width: 600px) and (max-width: 904px)");
  const isMobile = useMediaQuery("(max-width: 600px)");
  const [isOpen, setIsOpen] = React.useState(false); // golf modal open
  const [isCoronaModalOpen, setIsCoronaModalOpen] = React.useState(false); // corona modal open
  const [focusClass, setFocusClass] = React.useState(false);
  const [golfSelectKey, setGolfSelectKey] = React.useState<string>("0"); // for dropdown key [1,2]
  const [singleItemSelectKey, setSingleItemSelectKey] =
    React.useState<string>("0"); // for dropdown key [1,2]
  const [isMoreInfoOpen, setIsMoreInfoOpen] = React.useState(false);
  const { attributes } = useSelector((state: RootState) => state.quote);
  const { paymentDetails, gadgetMigration } = attributes;
  const isBeforeGadgetMigration = gadgetMigration?.isBeforeGadgetMigration;
  const freeACOCode =
    paymentDetails?.discounts?.offerDetails?.freeACOCode ?? undefined;
  const isQuoteDisabled = useQuoteDisabled();
  const { t } = useWizardTranslation();

  const { current } = useWizard();

  const golfYearPrice = useMemo(() => {
    if (application === "renewal") {
      return RENEWAL_GOLF_YEAR_PRICE[brand];
    }
    return GOLF_YEAR_PRICE[brand];
  }, [application]);

  const label = useMemo(() => {
    if (isSelect) {
      return addedBtnLabel ?? "Added";
    }
    return addBtnLabel ?? "Add";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelect, isTablet]);

  useEffect(() => {
    if (typeof isSelected === "string") {
      setGolfSelectKey(isSelected);
      setSingleItemSelectKey(isSelected);
    } else {
      setIsSelect(isSelected);
    }
  }, [isSelected]);

  const handleRemove = () => {
    setFocusClass(true);
    setIsOpen(true);
  };

  const handleToggle = () => {
    if (isTravelDisruption && !isSelect) {
      setIsCoronaModalOpen(true);
      setFocusClass(true);
    } else if (!isTravelDisruption && !isSelect) {
      if (onSelect !== undefined) {
        onSelect(true);
      }
    }
  };

  const handleGolfDropDownSelect = (e: string) => {
    if (onSelect !== undefined) {
      onSelect(e);
    }
  };

  const handleSingleItemDropDownSelect = (e: string) => {
    if (onSelect !== undefined) {
      onSelect(e);
    }
  };

  return (
    <>
      {/* REMOVE modal */}
      {isOpen && (
        <Modal
          onOpenChange={(e: boolean) => setIsOpen(e)}
          isOpen={isOpen}
          modalStyles={{
            top: `50%`,
            left: `50%`,
            transform: `translate(-50%, -50%)`,
          }}
          className={`bg-surface-neutral-option-1 md:max-w-[416px] max-w-[312px] relative rounded-radius-lg `}
          uderlayClassName="z-[100] bg-interactive-overlay-default"
        >
          <Dialog className=" relative">
            <RemoveModal
              title={removeModalTitle}
              onSelect={(e) => {
                setIsOpen(false);
                if (
                  onSelect !== undefined &&
                  !e &&
                  (golfSelectKey !== "0" || singleItemSelectKey !== "0")
                ) {
                  onSelect("0");
                } else if (onSelect !== undefined && !e) {
                  onSelect(e);
                }
              }}
              description={removeModalDescription}
              confirmCTALabel={removeModalConfirmCTALabel}
              cancelCTALabel={removeModalCancelCTALabel}
            />
          </Dialog>
        </Modal>
      )}

      {/* CORONA modal */}
      {isCoronaModalOpen && (
        <Modal
          onOpenChange={(e: boolean) => setIsCoronaModalOpen(e)}
          isOpen={isCoronaModalOpen}
          modalStyles={{
            top: `50%`,
            left: `50%`,
            transform: `translate(-50%, -50%)`,
          }}
          className={`bg-surface-neutral-option-1 max-w-[327px] md:max-w-[416px] relative rounded-radius-lg `}
          uderlayClassName="z-[100] bg-interactive-overlay-default"
        >
          <Dialog className=" relative">
            <CoronaModal
              onSelect={(e) => {
                setIsCoronaModalOpen(false);
                if (onSelect !== undefined && e) {
                  onSelect(e);
                }
              }}
              title={tdeModalTitle}
              description={tdeModalCopy}
              confirmCTALabel={tdeModalConfirmBtnLabel}
              rejectCTALabel={tdeModalCancelBtnLabel}
            />
          </Dialog>
        </Modal>
      )}

      {/* more info Drawer */}
      <MoreInfoDrawer
        isDraweOpenOpen={isMoreInfoOpen}
        setIsDraweOpenOpen={(e) => setIsMoreInfoOpen(e)}
        title={acoTitle}
        closeCta={drawerCloseCta}
        content={getDrawerContent(code, drawerContent, isBeforeGadgetMigration)}
      />

      <div
        data-testid="SingleACOSection-testid"
        className="flex justify-between self-stretch mb-spacing-xs md:mb-spacing-xxs
       md:min-h-[40px] min-h-[24px] lg:items-center md:items-center items-center"
      >
        <h1
          className={`${singleAcoClassNames.acoHeaderClassName} ${Styles.acoTitle} md:text-[25px] text-[18px] leading-[115%] text-text-functional`}
        >
          {acoTitle}
        </h1>
        {!isMobile && (
          <div className="h-[40px] max-h-[40px] md:flex md:flex-col md:items-center lg:block">
            {!isGolfCover && !isSingleItemCover && (
              <ToggleBtnLabelWrapper
                id={`toggle-button-label-${id}`}
                label={label}
                btnType="button"
                isActiveRightIcon={false}
                isDefaultActive={isSelect}
                isToggleEnable={false}
                className={`${
                  isSelect ? Styles.acoBtnActive : Styles.acoBtnInactive
                } w-[151px] md:min-w-[116px] md:w-fit lg:w-[151px] px-[12px]
               h-[40px] rounded-radius-full border-2 border-interactive-secondary-default
             ${
               isSelect
                 ? "!text-dark-text-on-secondary"
                 : "!text-interactive-secondary-default"
             } !justify-between items-center
             text-[18px] font-body-font leading-[100%] font-[600] min-h-[40px]
             ${
               isSelect
                 ? "!bg-interactive-secondary-default"
                 : "!bg-dark-text-on-secondary hover:!bg-interactive-action-hovered"
             } ${
               focusClass ? "focused-modal" : ""
             } disabled:cursor-not-allowed`}
                isLeftIcon
                leftIcon={
                  isSelect ? (
                    <CheckCircleIcon fill="white" stroke="#306582" />
                  ) : (
                    <></>
                  )
                }
                activeLeftIcon={
                  isSelect ? (
                    <CheckCircleIcon fill="white" stroke="#306582" />
                  ) : (
                    <></>
                  )
                }
                onClick={() => handleToggle()}
                onBlur={() => {
                  if (isTravelDisruption && !isCoronaModalOpen) {
                    setFocusClass(false);
                  }
                }}
                isDisabled={isQuoteDisabled}
              />
            )}

            {isGolfCover && (
              <div className={Styles.acoDropDownWrapper}>
                <ACODropdown
                  data-testid={`aco-dropdown-golf-cover-one-${id}`}
                  id={`aco-dropdown-golf-cover-one-${id}`}
                  isActiveLeftIcon
                  leftActiveIcon={
                    <CheckCircleIcon fill="white" stroke="#306582" />
                  }
                  selectedKey={golfSelectKey ?? "0"}
                  listClassName="mb-[2px] mx-[4px] px-[8px] p-spacing-xxs !bg-transparent !text-text-functional
                    rounded-radius-s hover:!bg-interactive-action-hovered focus:!bg-interactive-action-hovered"
                  unorderedListClassName={` ${
                    golfSelectKey === "0"
                      ? "md:w-[116px] lg:w-[151px]"
                      : "md:w-fit md:min-w-[151px]"
                  } py-[4px] mt-[-2px] border rounded-radius-xxl border-border-subdued 
                  !bg-surface-neutral-option-1 shadow-[0px_0px_12px_0px_rgba(15,23,42,0.11)]`}
                  placeholderText={`${t("choose", "Choose")}`}
                  className={` ${
                    golfSelectKey !== "0"
                      ? Styles.golfDropdownActive
                      : Styles.golfDropdownInactive
                  } ${
                    golfSelectKey === "0"
                      ? "md:w-[116px] lg:w-[151px]"
                      : "md:w-fit md:min-w-[151px]"
                  } px-[12px]
                    h-[40px] rounded-radius-full border-2 border-interactive-secondary-default
                  ${
                    golfSelectKey !== "0"
                      ? "!text-dark-text-on-secondary"
                      : "!text-interactive-secondary-default"
                  } !justify-between items-center
                  text-[18px] font-body-font leading-[100%] font-[600] 
                  ${
                    golfSelectKey !== "0"
                      ? "!bg-interactive-secondary-default"
                      : "!bg-surface-neutral-option-1"
                  } disabled:cursor-not-allowed disabled:border-border-disabled`}
                  onSelectionChange={(e: string) =>
                    handleGolfDropDownSelect(e as string)
                  }
                  isDisabled={isQuoteDisabled}
                >
                  {travelers.map((item, index) => {
                    const personOrPersons =
                      index === 0
                        ? t("person", "Person")
                        : t("persons", "Persons");
                    return (
                      <ACODropdownItem
                        data-testid="ACODropdownItem-testid-1"
                        value={`${index + 1} ${personOrPersons}`}
                        keyValue={`${index + 1}`}
                        key={item.sequence}
                      >
                        {`${index + 1} ${personOrPersons}`}
                      </ACODropdownItem>
                    );
                  })}
                </ACODropdown>
              </div>
            )}

            {isSingleItemCover && (
              <div className={Styles.acoDropDownWrapper}>
                <ACODropdown
                  data-testid={`aco-dropdown-golf-cover-one-${id}`}
                  id={`aco-dropdown-golf-cover-one-${id}`}
                  isActiveLeftIcon
                  leftActiveIcon={
                    <CheckCircleIcon fill="white" stroke="#306582" />
                  }
                  selectedKey={singleItemSelectKey ?? "0"}
                  listClassName="mb-[2px] mx-[4px] px-[8px] p-spacing-xxs !bg-transparent !text-text-functional  text-[18px] font-bold
                    rounded-radius-s hover:!bg-interactive-action-hovered focus:!bg-interactive-action-hovered"
                  unorderedListClassName={` ${
                    singleItemSelectKey === "0"
                      ? "md:w-[116px] lg:w-[151px]"
                      : "md:w-fit md:min-w-[151px]"
                  } py-[4px] mt-[-2px] border rounded-radius-xxl border-border-subdued 
                  !bg-surface-neutral-option-1 shadow-[0px_0px_12px_0px_rgba(15,23,42,0.11)]`}
                  placeholderText={t("choose", "Choose")}
                  className={` ${
                    singleItemSelectKey !== "0"
                      ? Styles.golfDropdownActive
                      : Styles.golfDropdownInactive
                  } ${
                    singleItemSelectKey === "0"
                      ? "md:w-[116px] lg:w-[151px]"
                      : "md:w-fit md:min-w-[151px]"
                  } px-[12px]
                    h-[40px] rounded-radius-full border-2 border-interactive-secondary-default
                  ${
                    singleItemSelectKey !== "0"
                      ? "!text-dark-text-on-secondary"
                      : "!text-interactive-secondary-default"
                  } !justify-between items-center
                  text-[18px] font-body-font leading-[100%] font-[600] 
                  ${
                    singleItemSelectKey !== "0"
                      ? "!bg-interactive-secondary-default"
                      : "!bg-surface-neutral-option-1"
                  } disabled:cursor-not-allowed disabled:border-border-disabled`}
                  onSelectionChange={(e: string) =>
                    handleSingleItemDropDownSelect(e as string)
                  }
                  isDisabled={isQuoteDisabled}
                >
                  {pricesUtil.map((price: string) => {
                    return (
                      <ACODropdownItem
                        data-testid="ACODropdownItem-testid-1"
                        value={`€${price}`}
                        key={price}
                        keyValue={price}
                      >
                        {`€${price}`}
                      </ACODropdownItem>
                    );
                  })}
                </ACODropdown>
              </div>
            )}

            {(golfSelectKey !== "0" ||
              singleItemSelectKey !== "0" ||
              (isSelect && code !== freeACOCode)) && (
              <div
                className={`w-[151px] ${
                  golfSelectKey !== "0" ? "md:w-[151px]" : "md:w-[116px]"
                } lg:w-[151px] text-text-critical text-[16px] font-body-font font-[600] leading-[19.49px] text-center md:mt-spacing-xs`}
              >
                <LabelButtonWrapper
                  data-testid={`aco-remove-button-${id}`}
                  id={`aco-remove-button-${id}`}
                  hierarchy="tertiary"
                  label={removeBtnLabel ?? "Remove"}
                  className={`${
                    Styles.removeBtn
                  } text-[16px] font-body-font font-[600] !px-0 md:leading-[16px] lg:leading-[19.49px] text-text-critical ${
                    focusClass ? "focused-modal" : ""
                  } disabled:cursor-not-allowed`}
                  onClick={() => {
                    handleRemove();
                  }}
                  onBlur={() => {
                    if (!isOpen) setFocusClass(false);
                  }}
                  isDisabled={isQuoteDisabled}
                />
              </div>
            )}
          </div>
        )}
      </div>
      <div className=" max-w-[100%] md:w-[298px]">
        {(type === SINGLE_TRIP ||
          (type === ANNUAL_MULTI_TRIP && priceMonth === null)) && (
          <div className="flex text-text-title w-full mb-spacing-xs">
            {isGolfCover &&
              (priceYear === 0 || singleItemSelectKey === "0") && (
                <p className="font-body-font text-[18px] leading-[150%] font-[400]">
                  {`${t("from", "From")}`}&nbsp;
                </p>
              )}
            {isSingleItemCover && singleItemSelectKey === "0" && (
              <p className="font-body-font text-[18px] leading-[150%] font-[400]">
                {`${t("from", "From")}`}&nbsp;
              </p>
            )}
            <p className="font-semibold-font text-[18px] leading-[150%] h-[27px] font-[600] items-center">
              {`${
                (current.brandSpecificParams?.currencySymbol as string) ?? ""
              }`}
              {isGolfCover && priceYear === 0
                ? golfYearPrice.toFixed(2)
                : priceYear.toFixed(2)}
            </p>
          </div>
        )}

        {type === ANNUAL_MULTI_TRIP && priceMonth !== null && (
          <div className="flex flex-col text-text-title w-full mb-spacing-xs">
            <div className="flex">
              {isGolfCover &&
                (priceYear === 0 || singleItemSelectKey === "0") && (
                  <p className="font-body-font text-[18px] leading-[150%] font-[400]">
                    From&nbsp;
                  </p>
                )}
              {isSingleItemCover && singleItemSelectKey === "0" && (
                <p className="font-body-font text-[18px] leading-[150%] font-[400]">
                  From&nbsp;
                </p>
              )}
              <p className="font-semibold-font text-[18px] leading-[150%] h-[27px] font-[600] items-center">
                {(current.brandSpecificParams?.currencySymbol as string) ?? ""}
                {isGolfCover && priceYear === 0
                  ? golfYearPrice.toFixed(2)
                  : priceYear.toFixed(2)}
              </p>
              <p className="font-body-font text-[18px] leading-[150%] font-[400]">
                &nbsp;in full or&nbsp;
              </p>
            </div>
            <div className="flex">
              <p className="font-semibold-font text-[18px] leading-[150%] h-[27px] font-[600] items-center">
                {`${
                  (current.brandSpecificParams?.currencySymbol as string) ?? ""
                }${priceMonth}`}
              </p>
              <p className="font-body-font text-[18px] leading-[150%] font-[400]">
                &nbsp;a month (total{" "}
                {(current.brandSpecificParams?.currencySymbol as string) ?? ""}
                {totalPCLCost})
              </p>
            </div>
          </div>
        )}

        <div className=" max-w-[100%] lg:mb-spacing-xs">
          <RichText
            richText={description}
            className="font-body-font font-[400] leading-[150%] text-[16px] text-text-functional w-[100%]"
          />
        </div>
        <div>
          <TextSelectionButton
            id={`aco-section-${id}`}
            data-testid={`aco-section-${id}`}
            label={moreInfoBtnLabel ?? "More info"}
            className={`${
              Styles.textSelectionButton
            } text-[16px] font-body-font font-[600] align-top lg:leading-[19.49px] leading-[150%]
                   hover:text-interactive-secondary-default hover:bg-surface-neutral-option-1 !px-0 ${
                     focusClass ? "focused-modal" : ""
                   }`}
            onClick={() => {
              setIsMoreInfoOpen(true);
              setFocusClass(true);
            }}
            onBlur={() => {
              if (!isMoreInfoOpen) setFocusClass(false);
            }}
            isDisabled={isQuoteDisabled}
          />
        </div>
      </div>

      {isMobile && (
        <div className="h-[40px] max-h-[40px] mt-spacing-xs flex items-center">
          {!isGolfCover && !isSingleItemCover && (
            <ToggleBtnLabelWrapper
              data-testid={`toggle-button-mobile-${id}`}
              id={`toggle-button-mobile-${id}`}
              label={label}
              btnType="button"
              isActiveRightIcon={false}
              isDefaultActive={isSelect}
              isToggleEnable={false}
              className={`${
                isSelect ? Styles.acoBtnActive : Styles.acoBtnInactive
              } w-[151px] md:w-[116px] lg:w-[151px] px-[12px]
               h-[40px] rounded-radius-full border-2 border-interactive-secondary-default
             ${
               isSelect
                 ? "!text-dark-text-on-secondary"
                 : "!text-interactive-secondary-default"
             } !justify-between items-center
             text-[18px] font-body-font leading-[100%] font-[600]  
             ${
               isSelect
                 ? "!bg-interactive-secondary-default"
                 : "!bg-dark-text-on-secondary hover:!bg-interactive-action-hovered"
             } ${focusClass ? "focused-modal" : ""}`}
              isLeftIcon
              leftIcon={
                isSelect ? (
                  <CheckCircleIcon fill="white" stroke="#306582" />
                ) : (
                  <></>
                )
              }
              activeLeftIcon={
                isSelect ? (
                  <CheckCircleIcon fill="white" stroke="#306582" />
                ) : (
                  <></>
                )
              }
              onClick={() => handleToggle()}
              onBlur={() => {
                if (isTravelDisruption && !isCoronaModalOpen) {
                  setFocusClass(false);
                }
              }}
              isDisabled={isQuoteDisabled}
            />
          )}

          {isGolfCover && (
            <div className={Styles.acoDropDownWrapper}>
              <ACODropdown
                id={`aco-dropdown-golf-cover-2-${id}`}
                isActiveLeftIcon
                leftActiveIcon={
                  <CheckCircleIcon fill="white" stroke="#306582" />
                }
                selectedKey={golfSelectKey ?? "0"}
                listClassName="mb-[2px] mx-[4px] px-[8px] p-spacing-xxs !bg-transparent !text-text-functional
                    rounded-radius-s hover:!bg-interactive-action-hovered focus:!bg-interactive-action-hovered"
                unorderedListClassName={`${
                  golfSelectKey === "0" ? "w-[151px]" : "w-fit min-w-[151px]"
                } py-[4px] mt-[-2px] border rounded-radius-xxl border-border-subdued
                !bg-surface-neutral-option-1 shadow-[0px_0px_12px_0px_rgba(15,23,42,0.11)] overflow-hidden`}
                placeholderText={t("choose", "Choose")}
                className={` ${
                  golfSelectKey !== "0"
                    ? Styles.golfDropdownActive
                    : Styles.golfDropdownInactive
                } ${
                  golfSelectKey === "0" ? "w-[151px]" : "w-fit min-w-[151px]"
                } px-[12px]
              h-[40px] rounded-radius-full border-2 border-interactive-secondary-default
            ${
              golfSelectKey !== "0"
                ? "!text-dark-text-on-secondary"
                : "!text-interactive-secondary-default"
            } !justify-between items-center
            text-[18px] font-body-font leading-[100%] font-[600] 
            ${
              golfSelectKey !== "0"
                ? "!bg-interactive-secondary-default"
                : "!bg-surface-neutral-option-1"
            } disabled:cursor-not-allowed disabled:border-border-disabled`}
                onSelectionChange={(e: string) =>
                  handleGolfDropDownSelect(e as string)
                }
                isDisabled={isQuoteDisabled}
              >
                {travelers.map((item, index) => {
                  const personOrPersons =
                    index === 0
                      ? t("person", "Person")
                      : t("persons", "Persons");
                  return (
                    <ACODropdownItem
                      data-testid="ACODropdownItem-testid-1"
                      value={`${index + 1} ${personOrPersons}`}
                      keyValue={`${index + 1}`}
                      key={item.sequence}
                    >
                      {`${index + 1} ${personOrPersons}`}
                    </ACODropdownItem>
                  );
                })}
              </ACODropdown>
            </div>
          )}

          {isSingleItemCover && (
            <div className={Styles.acoDropDownWrapper}>
              <ACODropdown
                id={`aco-dropdown-golf-cover-2-${id}`}
                isActiveLeftIcon
                leftActiveIcon={
                  <CheckCircleIcon fill="white" stroke="#306582" />
                }
                selectedKey={singleItemSelectKey ?? "0"}
                listClassName="mb-[2px] mx-[4px] px-[8px] p-spacing-xxs !bg-transparent !text-text-functional text-[18px] font-bold
                    rounded-radius-s hover:!bg-interactive-action-hovered focus:!bg-interactive-action-hovered"
                unorderedListClassName={`${
                  singleItemSelectKey === "0"
                    ? "w-[151px]"
                    : "w-fit min-w-[151px]"
                } py-[4px] mt-[-2px] border rounded-radius-xxl border-border-subdued
                !bg-surface-neutral-option-1 shadow-[0px_0px_12px_0px_rgba(15,23,42,0.11)] overflow-hidden`}
                placeholderText="Choose"
                className={` ${
                  singleItemSelectKey !== "0"
                    ? Styles.golfDropdownActive
                    : Styles.golfDropdownInactive
                } ${
                  singleItemSelectKey === "0"
                    ? "w-[151px]"
                    : "w-fit min-w-[151px]"
                } px-[12px]
              h-[40px] rounded-radius-full border-2 border-interactive-secondary-default
            ${
              singleItemSelectKey !== "0"
                ? "!text-dark-text-on-secondary"
                : "!text-interactive-secondary-default"
            } !justify-between items-center
            text-[18px] font-body-font leading-[100%] font-[600]
            ${
              singleItemSelectKey !== "0"
                ? "!bg-interactive-secondary-default"
                : "!bg-surface-neutral-option-1"
            } disabled:cursor-not-allowed disabled:border-border-disabled`}
                onSelectionChange={(e: string) =>
                  handleGolfDropDownSelect(e as string)
                }
                isDisabled={isQuoteDisabled}
              >
                {pricesUtil.map((price: string) => {
                  return (
                    <ACODropdownItem
                      data-testid="ACODropdownItem-testid-1"
                      value={`€${price}`}
                      key={price}
                      keyValue={price}
                    >
                      {`€${price}`}
                    </ACODropdownItem>
                  );
                })}
              </ACODropdown>
            </div>
          )}

          {(golfSelectKey !== "0" ||
            singleItemSelectKey !== "0" ||
            (isSelect && code !== freeACOCode)) && (
            <div className="ml-spacing-m text-text-critical text-[16px] font-body-font font-[600] leading-[19.49px] text-center flex items-center">
              <LabelButtonWrapper
                data-testid={`golf-remove-button-${id}`}
                id={`golf-remove-button-${id}`}
                hierarchy="tertiary"
                label={removeBtnLabel ?? "Remove"}
                className={`${
                  Styles.removeBtn
                } px-spacing-xxs text-[16px] font-body-font font-[600] leading-[19.49px] text-text-critical ${
                  focusClass ? "focused-modal" : ""
                }`}
                onClick={() => {
                  handleRemove();
                }}
                onBlur={() => {
                  if (!isOpen) setFocusClass(false);
                }}
                isDisabled={isQuoteDisabled}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default withApplication(SingleACOSection);
